import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ReactComponent as PassedIcon} from "../../../../../assets/images/validations/passed.svg";
import {ReactComponent as FailedIcon} from "../../../../../assets/images/validations/failed.svg";
import {Box, Button, CircularProgress, MenuItem, Popover, TextField, Tooltip, Typography} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../assets/images/remove.svg";
import IconButton from "../../../base/button/icon_button";
import {Add, ArrowForward, InfoOutlined, Remove} from "@mui/icons-material";
import {Col} from "reactstrap";
import debounce from "lodash/debounce";
import useIsMounted from "../../../../hooks/use-is-mounted";
import PartnerDataGrid, {DataGridColumnTypes, DataGridColumnWidthTypes, DataGridDensities} from "../../../../containers/data-grid";
import classnames from "classnames";
import Api from "../../../../../core/services/api/api";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {QuickOrderEntityPartNumberFieldStatus} from "../index";
import {formatMoney} from "../../../../../core/services/utils/utils";
import {getQuickOrderPartPendingKey, SuggestiveSellingInlinePending} from "../../../../features/suggestive-selling";

const QuickOrderItemElement = ({
                                   item,
                                   existingPartNos,
                                   warehouses,
                                   canRemove = true,
                                   isLast,
                                   onUpdate,
                                   onRemove,
                                   addItemOnTabCallBack,
                                   validatePartNo,
                                   getAvailabilityForPartNo: getAvailabilityForPartNoProps,
                                   getPriceForCustomerAndPartNo,
                                   selectedCustomer,
                                   pricingSectionEnabled = false,
                                   onPartFirstValidated,
                                   hasSuggestiveBeenOfferedForPart,
                               }) => {
    const operatorAccessLevels = useAccessLevels();
    const [creatingItemInSelectedWarehouse, setCreatingItemInSelectedWarehouse] = useState(false);
    const [fetchingAvailabilities, setFetchingAvailabilities] = useState(false);
    const [value, setValue] = useState({
        partNumber: item?.partNumber?.toUpperCase(),
        qty: item?.qty ?? 0,
        warehouseCode: item?.warehouseCode,
        price: item?.price,
    });
    const initialPartNumber = useRef(item?.partNumber?.toUpperCase());
    const suggestiveOfferedRef = useRef(false);
    const [partNumberFieldStatus, setPartNumberFieldStatus] = useState(QuickOrderEntityPartNumberFieldStatus.UnTouched);
    const [warehousesPopoverAnchor, setWarehousesPopoverAnchor] = useState(null);
    const isMounted = useIsMounted();

    const [customerPrice, setCustomerPrice] = useState(undefined);
    const [loadingCustomerPrice, setLoadingCustomerPrice] = useState(false);


    const orderMutatorDisabled = useMemo(() => partNumberFieldStatus !== QuickOrderEntityPartNumberFieldStatus.Valid, [partNumberFieldStatus])

    const invalid = useMemo(() =>
            [QuickOrderEntityPartNumberFieldStatus.Duplicate, QuickOrderEntityPartNumberFieldStatus.InValid,].includes(partNumberFieldStatus)
            || (value?.warehouseCode && (!!item?.availabilities?.find(e => e.warehouseCode === value?.warehouseCode)?.isComingSoon && operatorAccessLevels.packages.sales.manageWarehousesInQuote))
        , [item?.availabilities, operatorAccessLevels.packages.sales.manageWarehousesInQuote, partNumberFieldStatus, value?.warehouseCode])

    /**
     * Fetches the availabilities for the given part number
     * @type {(function(*, *=): Promise<void>)}
     */
    const getAvailabilitiesForPartNo = useCallback(async (partNo, forced = undefined) => {
        setFetchingAvailabilities(true);
        const availabilities = await getAvailabilityForPartNoProps(partNo, forced)
        if (!isMounted())
            return;
        setFetchingAvailabilities(false);
        onUpdate(item?.id, {availabilities});
    }, [getAvailabilityForPartNoProps, isMounted, item?.id, onUpdate])

    /**
     * Fetches the price for the customer and part number
     * @returns {Promise<void>}
     */
    const fetchPriceForPartNo = useCallback(async (selectedCustomerId, partNo) => {
        setLoadingCustomerPrice(true);
        const priceData = await getPriceForCustomerAndPartNo(selectedCustomerId, partNo);
        if (isMounted()) {
            setCustomerPrice(priceData?.price);
            setLoadingCustomerPrice(false);
        }
    }, [getPriceForCustomerAndPartNo, isMounted]);

    /**
     * Validates the entered part number in the text field as stated:
     *
     * - 1. if the given partNo is an empty string, we bring the state back to untouched
     * - 2. check if there are any duplicates of the provided part no. If duplicates exist, all the duplicates except
     * from the first one are invalid
     * - 3. check locally to determine validity
     * - 4. check the validity via the api call and if the api call was successful, sets the [hasError] in the inner
     * state to
     * @param {FocusEvent | string} partNo
     */
    const validatePartNumber = useCallback(async (partNo, automated = false) => {
        const _partNo = partNo?.trim()?.toUpperCase();
        // if the given partNo is an empty string, we bring the state back to untouched
        if (!_partNo?.length) {
            setPartNumberFieldStatus(QuickOrderEntityPartNumberFieldStatus.UnTouched);
            return onUpdate(item?.id, {isValid: undefined})
        }
        if (!automated) {
            // in here we update the part number in the state manager
            onUpdate(item?.id, {partNumber: _partNo});
        }

        if (!operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders) {
            const _partNumber = item?.partNumber?.toUpperCase() ?? '';
            // check if there are any duplicates of the provided part no. If duplicates exist, all the duplicates except
            // from the first one are invalid
            const duplicates = existingPartNos.filter(e => e.partNo === _partNo);
            const firstAmongDuplicate = duplicates?.findIndex(e => e.id === item?.id) === 0;
            if (!firstAmongDuplicate && ((duplicates.length > 0 && _partNo !== _partNumber) || (duplicates.length > 1 && _partNo === _partNumber))) {
                setPartNumberFieldStatus(QuickOrderEntityPartNumberFieldStatus.Duplicate);
                return onUpdate(item?.id, {isValid: false});
            }
        }

        setPartNumberFieldStatus(QuickOrderEntityPartNumberFieldStatus.Loading);
        const isValid = await validatePartNo(_partNo);
        if (!isMounted())
            return;

        if (isValid) {
            setPartNumberFieldStatus(QuickOrderEntityPartNumberFieldStatus.Valid);
            if (value.qty === 0) {
                setValue(prevState => ({...prevState, qty: Math.min(prevState.qty ?? 1, 1)}));
                onUpdate(item?.id, {qty: 1});
            }
        } else {
            setPartNumberFieldStatus(QuickOrderEntityPartNumberFieldStatus.InValid);
        }
        onUpdate(item?.id, {isValid});
        if (isValid) {
            void getAvailabilitiesForPartNo(_partNo);
            if (selectedCustomer?.id) {
                void fetchPriceForPartNo(selectedCustomer?.id, _partNo);
            }
            if (onPartFirstValidated && !suggestiveOfferedRef.current) {
                if (hasSuggestiveBeenOfferedForPart?.(_partNo)) {
                    suggestiveOfferedRef.current = true;
                    return;
                }
                suggestiveOfferedRef.current = true;
                void Api.getProductKeyInformation(_partNo).then((keyInfoResponse) => {
                    if (!keyInfoResponse?.resultFlag || !keyInfoResponse.data?.id) {
                        return;
                    }
                    const qty = Math.trunc(Number(value.qty) || 0);
                    void onPartFirstValidated({
                        partId: keyInfoResponse.data.id,
                        partNumber: _partNo,
                        sourceQuantity: qty > 0 ? qty : 1,
                    });
                });
            }
        } else {
            suggestiveOfferedRef.current = false;
        }
    }, [
        existingPartNos,
        item?.partNumber,
        hasSuggestiveBeenOfferedForPart,
        onPartFirstValidated,
        validatePartNo,
        getAvailabilitiesForPartNo,
        selectedCustomer?.id,
        fetchPriceForPartNo,
        isMounted,
        item?.id,
        onUpdate,
        value.qty,
        operatorAccessLevels.packages.sales.allowDuplicatePartNumbersInOrders,
    ])

    /**
     * With initial render of the component, if partNumber was passed in the props
     * checks for validation by calling [validatePartNumber] callback.
     */
    useEffect(() => {
        if (!item?.partNumber?.trim()?.length)
            return;
        setValue(prevState => ({
            ...prevState,
            partNumber: item?.partNumber?.trim()?.toUpperCase(),
            qty: item?.qty ?? 0,
            warehouseCode: item?.warehouseCode,
            price: item?.price,
        }));
        initialPartNumber.current = item?.partNumber?.trim()?.toUpperCase();
        validatePartNumber(item?.partNumber, true).then();
    }, [item?.partNumber])

    /**
     * Fetches the price for the customer and part number
     * whenever the part number field status changes to valid and the part number is not empty
     */
    useEffect(() => {
        const partNumber = item?.partNumber?.trim()?.toUpperCase();
        if (!pricingSectionEnabled || partNumberFieldStatus !== QuickOrderEntityPartNumberFieldStatus.Valid || !partNumber) {
            setCustomerPrice(undefined);
        } else if (pricingSectionEnabled && partNumberFieldStatus === QuickOrderEntityPartNumberFieldStatus.Valid && !!partNumber && selectedCustomer?.id) {
            void fetchPriceForPartNo(selectedCustomer?.id, partNumber);
        }
    }, [fetchPriceForPartNo, partNumberFieldStatus, pricingSectionEnabled, selectedCustomer?.id, item?.partNumber]);

    /**
     * Memo version of status Icon next to text field
     * @type {JSX.Element}
     */
    const statusIcon = useMemo(() => {
        if (operatorAccessLevels.packages.sales.manageWarehousesInQuote && !!value?.warehouseCode && (!!item?.availabilities?.find(e => e.warehouseCode === value?.warehouseCode)?.isComingSoon)) {
            return <FailedIcon/>;
        }
        switch (partNumberFieldStatus) {
            case QuickOrderEntityPartNumberFieldStatus.Valid:
                return <PassedIcon/>;
            case QuickOrderEntityPartNumberFieldStatus.InValid:
            case QuickOrderEntityPartNumberFieldStatus.Duplicate:
                return <FailedIcon/>;
            case QuickOrderEntityPartNumberFieldStatus.Loading:
                return <CircularProgress size={24}/>;
            default:
                return <div style={{width: '26px'}}/>
        }
    }, [item?.availabilities, partNumberFieldStatus, value?.warehouseCode, operatorAccessLevels.packages.sales.manageWarehousesInQuote])

    /**
     * Auto save callback function passed to debounce for saving the partNumber
     * @param arg
     */
    const onAutoSave = useCallback((arg) => {
        onUpdate(item?.id, arg);
    }, [item?.id, onUpdate])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceFunction = useCallback(debounce(onAutoSave, 300), [onAutoSave])

    /**
     * With each change in part number field value
     */
    const onPartNumberChanged = useCallback((e) => {
        const partNo = e.target?.value?.toUpperCase();
        const cursorPosition = e.target?.selectionStart;
        e.target.value = partNo;
        e.target.setSelectionRange(cursorPosition, cursorPosition);
        setValue(prevState => ({...prevState, partNumber: partNo}));
    }, [])

    /**
     * Handles blur event of part number text field
     * @type {(function(*): void)|*}
     */
    const handlePartNumberBlur = useCallback((e) => {
        const partNumber = e.target?.value?.trim()?.toUpperCase();
        if (partNumber !== initialPartNumber.current) {
            // we need to invalidate the price, specially since the price might be coming from importing.
            debounceFunction({price: undefined});
        }
        void validatePartNumber(e.target?.value);
    }, [debounceFunction, validatePartNumber])

    /**
     * Handles changes in the value of the quantity text field
     * @param newValue
     * @param isIncrease
     * @param isDecrease
     */
    const onSelectedQtyValueChanged = useCallback(({newValue, isIncrease, isDecrease}) => {
        const max = 9999;
        const min = operatorAccessLevels.packages.sales.issueCredit ? -9999 : 0;
        if (isIncrease) {
            setValue(prevState => ({...prevState, qty: prevState.qty < max ? prevState.qty + 1 : max}))
            if (value.qty < max) {
                debounceFunction({qty: value.qty + 1});
            }
            return;
        }

        if (isDecrease) {
            setValue(prevState => ({...prevState, qty: prevState.qty > min ? prevState.qty - 1 : min}))
            if (value.qty > min) {
                debounceFunction({qty: value.qty - 1});
            }
            return;
        }

        let _value = parseFloat(newValue);
        if (isNaN(_value)) {
            setValue(prevState => ({...prevState, qty: ''}))
            return;
        }
        if (!operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders) {
            _value = Math.trunc(_value);
        }
        if (!operatorAccessLevels.packages.sales.issueCredit) {
            _value = Math.max(_value, 0);
        }
        _value = _value > max ? max : _value < min ? min : _value;
        setValue(prevState => ({...prevState, qty: _value}))
        debounceFunction({qty: _value});
    }, [debounceFunction, operatorAccessLevels.packages.sales.issueCredit, value.qty, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Sets the quantity to 0 if the entered value is empty
     * @param event
     */
    const handleInvalidQty = useCallback((event) => {
        if (isNaN(parseFloat(event?.target?.value))) {
            setValue(prevState => ({...prevState, qty: 0}))
            debounceFunction({qty: 0});
        }
    }, [debounceFunction])

    /**
     * Sets the warehouse code in the state and calls the debounce function to save the warehouse code
     * @type {(function(*): void)}
     */
    const setSelectedWarehouse = useCallback((warehouse) => {
        setValue(prevState => ({...prevState, warehouseCode: warehouse?.code}));
        debounceFunction({warehouseCode: warehouse?.code});
    }, [debounceFunction])

    /**
     * Sets the price to the customer price
     * @type {(function(*): void)}
     */
    const setPriceToCustomerPrice = useCallback((price) => {
        setValue(prevState => ({...prevState, price}));
        onUpdate(item?.id, {price});
    }, [item?.id, onUpdate])

    /**
     * Handles changes in the value of the price text field
     * @type {(function(*): void)|*}
     */
    const onSelectedPriceChanged = useCallback((newPrice) => {
        setValue(prevState => ({...prevState, price: newPrice}));
    }, [])

    /**
     * Handles changes in the value of the price text field
     * @type {(function(*): void)|*}
     */
    const handleInvalidPrice = useCallback((event) => {
        const stringVal = event?.target?.value?.trim();
        let priceValue = parseFloat(stringVal);
        if ((isNaN(priceValue) || priceValue < 0) && !!stringVal) {
            priceValue = 0;
        } else if (!stringVal) {
            priceValue = null;
        }
        setValue(prevState => ({...prevState, price: priceValue}))
        debounceFunction({price: priceValue});
    }, [debounceFunction])


    const errorMessage = useMemo(() => {
        switch (partNumberFieldStatus) {
            case QuickOrderEntityPartNumberFieldStatus.InValid:
                return "Invalid Part number"
            case QuickOrderEntityPartNumberFieldStatus.Duplicate:
                return "Duplicated Part number"
            default:
                if (value?.warehouseCode && (!!item?.availabilities?.find(e => e.warehouseCode === value?.warehouseCode)?.isComingSoon && operatorAccessLevels.packages.sales.manageWarehousesInQuote)) {
                    return "Create the Part in the selected warehouse";
                }
                return '';
        }
    }, [item?.availabilities, operatorAccessLevels.packages.sales.manageWarehousesInQuote, partNumberFieldStatus, value?.warehouseCode])

    const dataGridColumns = useMemo(() =>
        /** @type{DataGridColumn[]}*/
        [
            {
                name: "warehouse",
                title: "Warehouse",
                type: DataGridColumnTypes.string,
                sortable: false,
                reorderable: false,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                savable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
            },
            {
                name: "availability",
                title: "Qty",
                type: DataGridColumnTypes.string,
                sortable: false,
                reorderable: false,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                savable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
            },
        ], [])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        if (!item?.availabilities?.length)
            return [];
        /**
         * @type {DataGridRow[]}
         */
        return item.availabilities.map((availability) => ({
            key: `${availability.warehouseName ?? ""} (${availability.warehouseCode ?? ""})`,
            data: availability,
            cells: {
                'warehouse': `${availability.warehouseName ?? ""} (${availability.warehouseCode ?? ""})`,
                'availability': availability.isComingSoon ? "Coming Soon" : availability.availableQty,
            },
        }))
    }, [item?.availabilities])

    const selectedAvailabilityWarehouse = useMemo(() => item.availabilities?.find(e => e.warehouseCode === value?.warehouseCode), [item.availabilities, value?.warehouseCode])
    const selectedWarehouse = useMemo(() => warehouses?.find(e => e.code === value?.warehouseCode), [value?.warehouseCode, warehouses])
    const suggestivePendingKey = useMemo(
        () => (value?.partNumber?.trim() ? getQuickOrderPartPendingKey(value.partNumber) : null),
        [value?.partNumber],
    )

    /**
     * Creates the item in the selected warehouse
     * @type {(function(): Promise<void>)}
     */
    const createItemInSelectedWarehouse = useCallback(async () => {
        if (!selectedAvailabilityWarehouse || !selectedAvailabilityWarehouse?.isComingSoon || !selectedWarehouse)
            return;
        setCreatingItemInSelectedWarehouse(true);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [value.partNumber],
        });
        if (!isMounted())
            return;
        if (!partInfoResponse?.resultFlag) {
            setCreatingItemInSelectedWarehouse(false);
            return;
        }
        const forApi = {
            partId: partInfoResponse.data?.find(e => e.partNo === value.partNumber)?.id,
            warehouseId: selectedWarehouse?.id,
        }
        const response = await Api.createPartInWarehouse(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await getAvailabilitiesForPartNo(value.partNumber, true);
        }
        if (!isMounted())
            return;
        setCreatingItemInSelectedWarehouse(false)
    }, [getAvailabilitiesForPartNo, isMounted, selectedAvailabilityWarehouse, selectedWarehouse, value.partNumber])

    return (
        <>
            <div className={'quick-order-entity-card'}>
                <div className={'d-flex flex-wrap w-100'}>
                    <Col xs={true} className={'d-flex align-content-start justify-content-start'} style={{gap: 8}}>
                        {
                            operatorAccessLevels.packages.sales.manageWarehousesInQuote &&
                            <Button
                                disabled={!warehouses.length}
                                sx={{backgroundColor: 'background.default', height: 37, minWidth: 37, width: 37, color: 'primary.main'}}
                                onClickCapture={(e) => setWarehousesPopoverAnchor(e.currentTarget)}
                            >
                                {value?.warehouseCode ?? '--'}
                            </Button>
                        }
                        <TextField
                            size={'small'}
                            value={value?.partNumber ?? ''}
                            label={"Part Number"}
                            error={invalid}
                            helperText={errorMessage}
                            onBlur={handlePartNumberBlur}
                            onChange={(e) => onPartNumberChanged(e)}
                        />
                        {
                            statusIcon &&
                            <Box
                                sx={{height: 37, minWidth: 37, width: 37, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                {statusIcon}
                            </Box>
                        }
                        <SuggestiveSellingInlinePending pendingKey={suggestivePendingKey} showLabel={false}/>
                    </Col>
                    <Col xs={'auto'} className={'d-flex align-items-center justify-content-center'}>
                        {
                            !!selectedAvailabilityWarehouse && !!selectedAvailabilityWarehouse?.isComingSoon &&
                            partNumberFieldStatus === QuickOrderEntityPartNumberFieldStatus.Valid &&
                            <Button
                                onClick={() => createItemInSelectedWarehouse()}
                                disabled={creatingItemInSelectedWarehouse}
                                variant={'outlined'}
                                color={'primary'}
                            >
                                {
                                    creatingItemInSelectedWarehouse
                                        ? "Creating in warehouse..."
                                        : "Create in warehouse"
                                }
                            </Button>
                        }
                        {
                            !selectedAvailabilityWarehouse?.isComingSoon &&
                            <>
                                <IconButton
                                    onPressed={() => !orderMutatorDisabled && onSelectedQtyValueChanged({isDecrease: true})}
                                    color={'secondary'}
                                >
                                    <Remove className={'icon small'}/>
                                </IconButton>
                                <div className={'quantity-field mx-2'}>
                                    <TextField
                                        size={'small'}
                                        fullWidth
                                        disabled={orderMutatorDisabled}
                                        inputProps={{min: -9999, max: 9999}}
                                        type={'number'}
                                        placeholder={''}
                                        sx={{
                                            '& input': {
                                                textAlign: 'center',
                                            }
                                        }}
                                        value={value.qty}
                                        onBlur={handleInvalidQty}
                                        onChange={(e) => onSelectedQtyValueChanged({newValue: e.target.value})}
                                    />
                                </div>
                                <IconButton
                                    onPressed={() => !orderMutatorDisabled && onSelectedQtyValueChanged({isIncrease: true})}
                                    color={'secondary'}
                                >
                                    <Add className={'icon small'}/>
                                </IconButton>
                            </>
                        }
                    </Col>
                    <Col xs={'auto'} className={'d-flex align-items-center justify-content-end'}>
                        {
                            canRemove &&
                            <IconButton
                                onPressed={() => onRemove(item?.id)}
                                color={'paper.error'}
                                onKeyDown={isLast ? addItemOnTabCallBack : () => false}
                            >
                                <RemoveIcon className={'remove-icon'}/>
                            </IconButton>
                        }
                    </Col>
                    <Col xs={12} className={classnames('d-flex flex-column px-2', {'mt-3': partNumberFieldStatus === QuickOrderEntityPartNumberFieldStatus.Valid})}>
                        {
                            partNumberFieldStatus === QuickOrderEntityPartNumberFieldStatus.Valid && pricingSectionEnabled &&
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between'}}>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5}}>
                                    <Box sx={{width: 48}}/>
                                    {
                                        !!selectedCustomer?.id &&
                                        <Typography variant={'caption'} fontWeight={500} sx={{mr: 1}}>
                                            Customer Price: {loadingCustomerPrice ? <CircularProgress size={14}/> : (!!customerPrice ? formatMoney(customerPrice) : 'N/A')}
                                        </Typography>
                                    }
                                    {
                                        !!selectedCustomer?.id &&
                                        <IconButton
                                            disabled={loadingCustomerPrice || !customerPrice}
                                            color={'paper.secondary'}
                                            onPressed={() => setPriceToCustomerPrice(customerPrice)}
                                        >
                                            <ArrowForward className='icon normal'/>
                                        </IconButton>
                                    }
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                                    <TextField
                                        size={'small'}
                                        label={'Price'}
                                        type={'number'}
                                        value={value.price ?? ''}
                                        onChange={(e) => onSelectedPriceChanged(e.target.value)}
                                        onBlur={handleInvalidPrice}
                                        sx={{width: 150}}
                                        InputProps={{
                                            startAdornment: <Typography variant={'body1'} sx={{mr: 0.5}}>$</Typography>,
                                            ...(customerPrice && value.price !== customerPrice && {
                                                endAdornment:
                                                    <Tooltip title={!value.price ? "Price is not set yet, customer price will be used" : "Customer price is different than the entered price"}>
                                                        <InfoOutlined color={'warning'} fontSize={'small'}/>
                                                    </Tooltip>
                                            }),
                                        }}
                                    />
                                    <Box sx={{width: 64}}/>
                                </Box>
                            </Box>
                        }
                    </Col>
                    <Col xs={12} className={classnames('d-flex flex-column px-2', {'mt-3': partNumberFieldStatus === QuickOrderEntityPartNumberFieldStatus.Valid && !!item?.availabilities?.length})}>
                        {
                            fetchingAvailabilities &&
                            <>
                                <div className={'loading extraSmall my-1'}>
                                    <div/>
                                </div>
                                <div className={'loading extraSmall my-1'}>
                                    <div/>
                                </div>
                                <div className={'loading extraSmall my-1'}>
                                    <div/>
                                </div>
                                <div className={'loading extraSmall my-1'}>
                                    <div/>
                                </div>
                            </>
                        }
                        {
                            !fetchingAvailabilities &&
                            partNumberFieldStatus === QuickOrderEntityPartNumberFieldStatus.Valid && !!item?.availabilities?.length &&
                            <PartnerDataGrid
                                hideToolbar
                                hideFooter
                                hidePagination
                                hidePageSize
                                hideSelectAll
                                visibleRows={3}
                                classNames={{
                                    container: "quick-order-element-quantities-data-grid"
                                }}
                                columns={dataGridColumns}
                                rows={dataGridRows}
                                state={{
                                    density: DataGridDensities.compact,
                                }}
                            />
                        }
                    </Col>
                </div>
            </div>
            <Popover
                open={!!warehousesPopoverAnchor}
                onClose={() => setWarehousesPopoverAnchor(null)}
                anchorEl={warehousesPopoverAnchor}
                elevation={1}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: 200,
                        padding: 1,
                    }}
                >
                    <Typography variant={'body2'} fontWeight={600} sx={{marginBlockEnd: 1}}>
                        Select your Warehouse
                    </Typography>
                    {
                        warehouses?.map((e) => (
                            <MenuItem
                                key={e.code}
                                value={e}
                                selected={value?.warehouseCode === e?.code}
                                onClick={() => {
                                    setWarehousesPopoverAnchor(null);
                                    setSelectedWarehouse(e);
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    width: 'fill-available',
                                    borderRadius: 1,
                                    marginInline: 0,
                                    padding: 1,
                                    '.fill': {
                                        color: 'primary.main',
                                    }
                                }}
                            >
                                <Typography variant={'caption'}>
                                    {e.name ?? ""} <Typography
                                    sx={{display: 'inline-block'}} variant={'body2'} color={'primary'} fontWeight={600}
                                >
                                    ({e.code})
                                </Typography>
                                </Typography>
                            </MenuItem>
                        ))
                    }
                </Box>
            </Popover>
        </>
    );
}

export default QuickOrderItemElement;
